// Library methods
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Contexts
import { useDevicesContext } from "src/contexts/devices/DevicesContext";
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Styles
import { useTheme } from "@mui/material/styles";

const LiveUpdateBanner = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();

  const { devices, isLoadingDevices, isErrorDevices } = useDevicesContext();
  const { actions } = useLiveUpdateContext();
  const { handleViewLiveUpdateClick } = actions;

  // show the banner if at least one device has an in-progress exam
  const showBanner: boolean = useMemo(
    () =>
      Boolean(
        devices?.length &&
          devices?.some(
            (device) =>
              device?.associatedExam?.exam &&
              device?.deviceStatus?.stage?.stageType
          ) &&
          !isLoadingDevices &&
          !isErrorDevices &&
          (location.pathname.includes("patients") ||
            location.pathname.includes("dashboard"))
      ),
    [devices, isLoadingDevices, isErrorDevices, location.pathname]
  );

  const devicesWithExams = useMemo(
    () =>
      (devices ?? [])
        ?.filter(
          (device) =>
            device?.associatedExam?.exam &&
            device?.deviceStatus?.stage?.stageType
        )
        ?.slice(0, 2),
    [devices]
  );

  return (
    <Grid
      item
      bgcolor={theme.palette.mode === "dark" ? "#3B868E" : "#48BECB"}
      width="100%"
      padding="8px"
      display={showBanner ? "flex" : "none"}
      alignItems="center"
      justifyContent="center"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          color={theme.palette.mode === "dark" ? "#FFF" : "#000"}
          textAlign="center"
          fontSize={{ xs: "12px", sm: "14px" }}
        >
          {t("Exam in progress. Click on the device name to see more", {
            count: devicesWithExams?.length,
          })}
          :
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            columnGap: "16px",
          }}
        >
          {devicesWithExams?.map((device) => (
            <Typography
              key={device?.id}
              color={theme.palette.mode === "dark" ? "#FFF" : "#000"}
              textAlign="center"
              fontSize={{ xs: "12px", sm: "14px" }}
            >
              <span
                role="button"
                onClick={() => handleViewLiveUpdateClick(device?.id)}
                style={{
                  textDecoration: "underline",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {device?.alias}
              </span>
            </Typography>
          ))}
        </Box>
      </Box>
    </Grid>
  );
};

export default LiveUpdateBanner;
